import React from "react"
import { useTranslation } from "react-i18next"
import {
  ProductAssociationApplyPopup,
  type ProductAssociationLabels,
  type ProductAssociationRequest,
} from "../../../components/Associations/ProductAssociationApplyPopup"
import { ESelectionApplyTargetType } from "../../../Enum/ESelectionApplyTargetType"
import type { ApplySelectionRequest } from "../../../hooks/Selection/useApplySelection"

interface SelectionApplyPopupProps {
  title: string
  actionLabel: string
  helpTitle: string
  helpDescription: string
  helpBullets: string[]
  helpKey: string
  onClose: () => void
  onConfirm: (request: ApplySelectionRequest) => Promise<void>
}

export const SelectionApplyPopup: React.FC<SelectionApplyPopupProps> = ({ onConfirm, ...props }) => {
  const { t } = useTranslation("selection")

  const labels: ProductAssociationLabels = {
    targetLegend: t("apply_target_legend"),
    allProducts: t("apply_target_all_products"),
    selectedProducts: t("apply_target_selected_products"),
    allCatalogItems: t("apply_target_all_catalogItems"),
    selectedCatalogItems: t("apply_target_selected_catalogItems"),
    allCombos: t("apply_target_all_combos"),
    selectedCombos: t("apply_target_selected_combos"),
    selectProductsAction: t("apply_select_products_action"),
    selectCatalogItemsAction: t("apply_select_catalogItems_action"),
    selectCombosAction: t("apply_select_combos_action"),
    selectedProductsCount: t("apply_selected_products"),
    selectedCatalogItemsCount: t("apply_selected_catalogItems"),
    selectedCombosCount: t("apply_selected_combos"),
    selectedProductsTitle: t("apply_target_selected_products"),
    selectedCatalogItemsTitle: t("apply_target_selected_catalogItems"),
    selectedCombosTitle: t("apply_target_selected_combos"),
    noSelectedProducts: t("apply_no_selected_products"),
    noSelectedCatalogItems: t("apply_no_selected_catalogItems"),
    noSelectedCombos: t("apply_no_selected_combos"),
  }

  const handleConfirm = (request: ProductAssociationRequest) =>
    onConfirm({ ...request, targetType: request.targetType as ESelectionApplyTargetType })

  return (
    <ProductAssociationApplyPopup
      {...props}
      labels={labels}
      targetValues={{
        allProducts: ESelectionApplyTargetType.AllProducts,
        selectedProducts: ESelectionApplyTargetType.SelectedProducts,
        allCatalogItems: ESelectionApplyTargetType.AllCatalogItems,
        selectedCatalogItems: ESelectionApplyTargetType.SelectedCatalogItems,
        allCombos: ESelectionApplyTargetType.AllCombos,
        selectedCombos: ESelectionApplyTargetType.SelectedCombos,
      }}
      interactionKeys={{
        closeDescriptionKey: "interactionDescriptions:pages.products.selection.selectionApplyPopup.button.onClose",
        targetDescriptionKey:
          "interactionDescriptions:pages.products.selection.selectionApplyPopup.select.applyTargetLegend",
        confirmDescriptionKey: "interactionDescriptions:pages.products.selection.selectionApplyPopup.button.actionLabel",
        addProductDescriptionKey: "interactionDescriptions:pages.products.selection.selectionApplyPopup.button.addProduct",
        removeProductDescriptionKey:
          "interactionDescriptions:pages.products.selection.selectionApplyPopup.button.removeProduct",
        addCatalogItemDescriptionKey:
          "interactionDescriptions:pages.products.selection.selectionApplyPopup.button.addCatalogItem",
        removeCatalogItemDescriptionKey:
          "interactionDescriptions:pages.products.selection.selectionApplyPopup.button.removeCatalogItem",
        addComboDescriptionKey: "interactionDescriptions:pages.products.selection.selectionApplyPopup.button.addCombo",
        removeComboDescriptionKey:
          "interactionDescriptions:pages.products.selection.selectionApplyPopup.button.removeCombo",
      }}
      onConfirm={handleConfirm}
    />
  )
}
